import React, { useEffect, useState } from 'react';
import { Button, Form, Row, Col, Modal, Upload, Input, Space, UploadFile, Select } from 'antd';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { InboxOutlined, UploadOutlined } from '@ant-design/icons';

//Components
const { Option } = Select;
import { updateDrawing, updateFile } from '../../Network/Core/Directory/DirectoryServices';
import { getErrorMessage } from '../../Utils/errorHelpers';

interface FormValues {
    name: string | null;
}

const EditFileFormComponent = ({ file, siblingNames = [] }: { file: any; siblingNames?: string[]; FormTitle?: string }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<FormValues>({ name: file.name });
    const [form] = Form.useForm();
    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    const values = Form.useWatch([], form);

    const [uploadForm] = Form.useForm();
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);

    useEffect(() => {
        form.validateFields({ validateOnly: true }).then(
          () => setIsFormValid(true),
          () => setIsFormValid(false),
        );
    }, [values]);

    useEffect(() => {
        setFormData({ name: file.name });
    }, []);

    const saveFile = async () => {
        if (formData.name !== '') {
            try {
                setIsFormValid(false);
                await updateFile(file.id, formData);
                toast.success('File updated successfully');
                navigate(-1);
            } catch (error: any) {
                toast.error(getErrorMessage(error, 'Failed to update file'));
                setIsFormValid(true);
            }
        } else {
            toast.error('Fill Required Fields');
        }
    };

    const handleFileChange = async ({ fileList }: {fileList: UploadFile[]}) => {
        // Ensure only the last uploaded file is kept
        setFileList(fileList.slice(-1));
    };

    const beforeUpload = (file: UploadFile) => {
        // Replace existing file with new one
        const fileSize = file.size || 0

        if (fileSize > 1024*1024*20) {
            uploadForm.setFields([
                {
                    name: 'file',
                    errors: ['File must be smaller than 20MB']
                }
            ]);
            return Upload.LIST_IGNORE; // Prevent the file from being added to the list
        }

        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        const allowedExtensions = ['.dwg'];
        if (!allowedExtensions.includes(fileExtension)) {
            uploadForm.setFields([
                {
                    name: 'file',
                    errors: ['File type is not supported. Please upload a valid DWG file.'],
                },
            ]);
            return Upload.LIST_IGNORE;
        }

        setFileList([file]);
        return false; // Prevent auto upload
    };

    const isDuplicateName = formData.name?.trim() !== file.name && siblingNames.includes(formData.name?.trim() || '');

    const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [event.target.name]: event.target.value,
        });
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        await saveFile();
    };

    const backToFileList = () => {
        navigate(-1);
    };

    const handleUploadCancel = () => {
        setShowUploadModal(false);
    };

    const handleUploadOk = async () => {
        try {
            setUploading(true);
            const values = await uploadForm.validateFields();

            const uploadedFile = values.file?.fileList?.[0]?.originFileObj;

            if (!uploadedFile) {
                toast.error("File upload failed");
                console.error("No file selected");
                return;
            }

            const formData = new FormData();
            formData.append("simplifyThreshold", values.simplifyThreshold);
            formData.append("file", uploadedFile); // Correct way to append file

            await updateDrawing(file.id, formData);
            setFileList([]);

            toast.success("File uploaded successfully");
            toast.warning("Please make sure the drawing was updated")
            setShowUploadModal(false);
        } catch (error) {
            toast.error("File upload failed");
            console.error("Upload error:", error);
        } finally {
            setUploading(false);
        }
    };


    return (
      <>
          <Form
            className="common-form"
            layout="vertical"
            form={form}
            onSubmitCapture={handleSubmit}
          >
              <Row gutter={{ xs: 8, sm: 16, md: 20, lg: 30 }}>
                  <Col xs={24} sm={24} md={12} lg={8}>
                      {/* Wrapping input and upload button in a single row */}
                      <Form.Item
                        label="Name"
                        required
                        validateStatus={isDuplicateName ? 'error' : ''}
                        help={isDuplicateName ? 'A file with this name already exists' : ''}
                      >
                          <Space.Compact style={{ width: '100%' }}>
                          <Input
                                name="name"
                                placeholder="Enter Name"
                                value={formData.name ?? ''}
                                onChange={handleFormChange}
                                status={isDuplicateName ? 'error' : ''}
                                style={{ width: 'calc(100% - 170px)' }} // Adjusted to fit wider button
                              />
                              <Button
                                type="dashed" // Dashed style makes it look less aggressive
                                icon={<UploadOutlined />}
                                onClick={() => setShowUploadModal(true)}
                                style={{
                                    width: '160px', // Wider for better readability
                                    marginLeft: '10px', // More spacing between input and button
                                    backgroundColor: '#f9f9f9',
                                    borderColor: '#d9d9d9',
                                    color: '#000',
                                }}
                              >
                                  Replace DWG File
                              </Button>
                          </Space.Compact>
                      </Form.Item>
                  </Col>
              </Row>

              <Form.Item>
                  <div className="bottom-btn-section">
                      <Button className="btn-link" onClick={backToFileList}>
                          Cancel
                      </Button>
                      <Button
                        className="common-btn"
                        htmlType="submit"
                        disabled={!isFormValid || isDuplicateName}
                      >
                          Save
                      </Button>
                  </div>
              </Form.Item>
          </Form>

          {/* Upload file modal */}
          <Modal
            centered
            title="Upload File"
            footer={null}
            open={showUploadModal}
            onOk={handleUploadOk}
            onCancel={handleUploadCancel}
          >
              <Form
                form={uploadForm}
                layout="vertical"
                preserve={false}
                className="common-form"
                name="upload-file-form"
                onFinish={handleUploadOk}
                autoComplete="off"
              >

                  <Form.Item
                    label="Curved Element Resolution"
                    name="simplifyThreshold"
                    rules={[{ required: true, message: 'Please select a Resolution!' }]}
                    initialValue={0.001}>
                      <Select placeholder="Select Resolution">
                          <Option value={0.0001}>High</Option>
                          <Option value={0.001}>Default</Option>
                          <Option value={0.01}>Low</Option>
                      </Select>
                  </Form.Item>

                  <div style={{ color: 'rgba(0,0,0,0.45)' }}>
                      Higher value means higher accuracy but also adds complexity. This will affect file size and performance.
                  </div>

                  <Form.Item
                    name="file"
                    rules={[
                        {
                            required: true,
                            message: 'Please select the drawing file',
                        },
                    ]}
                  >
                      <Upload.Dragger
                        accept=".dwg"
                        fileList={fileList}
                        maxCount={1} // Limits to 1 file
                        beforeUpload={beforeUpload}
                        onChange={handleFileChange}
                      >
                          <p className="ant-upload-drag-icon">
                              <InboxOutlined />
                          </p>
                          <p className="ant-upload-text">Click or drag file to this area to upload</p>
                      </Upload.Dragger>
                  </Form.Item>

                  <div className="form-btns">
                      <Button className="self-btn" onClick={handleUploadCancel}>
                          Cancel
                      </Button>
                      <Button
                        className="common-btn"
                        type="primary"
                        htmlType="submit"
                        disabled={!isFormValid}
                        loading={uploading}
                      >
                          Upload
                      </Button>
                  </div>
              </Form>
          </Modal>
      </>
    );
};

export default EditFileFormComponent;